import { effect, inject } from '@angular/core';
import { MapApi } from '@app/api/map.api';
import { MapTrackResponse } from '@app/models';
import { patchState, signalStore, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { forkJoin, map, of, pipe, switchMap, tap } from 'rxjs';
import { OrdersStore } from './orders.store';

interface MapState {
  activeOrdersById: Record<number, MapTrackResponse> | null;
  isActiveOrdersLoading: boolean;
  orderByToken: MapTrackResponse | null;
  orderByTokenIsLoading: boolean;
}

const initialState: MapState = {
  activeOrdersById: null,
  isActiveOrdersLoading: false,
  orderByToken: null,
  orderByTokenIsLoading: false,
};

export const MapStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),

  withMethods((store) => {
    const mapApi = inject(MapApi);
    const ordersStore = inject(OrdersStore);

    const loadActiveOrders = rxMethod<number[]>(
      pipe(
        tap(() => patchState(store, { isActiveOrdersLoading: true })),
        switchMap((ids) => {
          if (ids.length === 0) {
            return of([]);
          }

          return forkJoin(
            ids.map((id) =>
              mapApi
                .getOrderOnMap$(id)
                .pipe(map((response) => ({ id, response }))),
            ),
          );
        }),
        tap((results) => {
          const mapped = results.reduce(
            (acc, { id, response }) => {
              acc[id] = response;
              return acc;
            },
            {} as Record<number, MapTrackResponse>,
          );

          patchState(store, (state) => ({
            activeOrdersById: {
              ...(state.activeOrdersById ?? {}),
              ...mapped,
            },
            isActiveOrdersLoading: false,
          }));
        }),
      ),
    );

    const loadOrderByToken = rxMethod<string>(
      pipe(
        tap(() => patchState(store, { orderByTokenIsLoading: true })),
        switchMap((token) => mapApi.getOrderOnMapByToken$(token)),
        tap((orderByToken) =>
          patchState(store, { orderByToken, orderByTokenIsLoading: false }),
        ),
      ),
    );

    const clearOrderByToken = () =>
      patchState(store, {
        orderByToken: null,
        orderByTokenIsLoading: false,
      });

    effect(
      () => {
        const orders = ordersStore.activeOrders();
        const current = store.activeOrdersById() ?? {};

        if (!orders.length) {
          return;
        }

        const ids = orders.map((o) => o.id);

        const missingIds = ids.filter((id) => !current[id]);

        if (!missingIds.length) {
          return;
        }

        loadActiveOrders(missingIds);
      },
      { allowSignalWrites: true },
    );

    const clearMap = () => patchState(store, initialState);

    return {
      loadActiveOrders,
      loadOrderByToken,
      clearOrderByToken,
      clearMap,
    };
  }),
);
